import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Shield, Heart, Eye, Lightbulb, Users, Globe, Target, Zap, BookOpen, CheckCircle, BadgeCheck, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";

const values = [
  {
    icon: <Eye className="h-6 w-6" />,
    title: "Transparency",
    description: "We document every data source, every limitation, and every methodology. If we can't explain it publicly, we don't use it.",
  },
  {
    icon: <Heart className="h-6 w-6" />,
    title: "Honesty",
    description: "We don't exaggerate what our tool can do. We tell you what we check, what we can't check, and where our limitations are.",
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Privacy",
    description: "We check domains, not people. No accounts, no tracking, no data collection. Your privacy isn't a feature — it's a right.",
  },
  {
    icon: <Lightbulb className="h-6 w-6" />,
    title: "Education",
    description: "We want you to understand online safety — not depend on us. Our blog and guides teach you to spot scams yourself.",
  },
];

export default function About() {
  return (
    <Layout>
      <Helmet>
        <title>About Us — Who Built Transparency Badge</title>
        <meta name="description" content="Who built Transparency Badge and why — a free website safety checker plus a Verified Badge Program that helps legitimate site owners earn trust." />
        <meta property="og:title" content="About Us — Who Built Transparency Badge" />
        <meta property="og:description" content="Meet the team behind Transparency Badge and learn why we built a free website safety checker and Verified Badge Program." />
        <meta property="og:url" content="https://transparencybadge.com/about" />
        <meta property="og:image" content="https://transparencybadge.com/og-image.png" />
        <link rel="canonical" href="https://transparencybadge.com/about" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="About Us"
            title="Who We Are and Why We Built This"
            description="We built a free tool that helps people check if a website is safe — and a program that helps legitimate website owners prove it."
          />
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          <div className="bg-card rounded-2xl border p-8 md:p-10">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Globe className="h-6 w-6 text-primary" />
              The Problem We're Solving
            </h2>
            <p className="text-muted-foreground mb-4">
              Online scams cost people billions of dollars every year. Phishing sites, fake stores, and credential-stealing pages are getting harder to spot — many of them look indistinguishable from real websites. Even people who consider themselves tech-savvy fall for them.
            </p>
            <p className="text-muted-foreground mb-4">
              The data to check if a website is safe exists — scattered across threat databases, domain registries, and SSL authorities. But most people don't have access to it, don't know where to look, and don't have time to check multiple sources manually.
            </p>
            <p className="text-muted-foreground mb-4">
              That's why we built the Transparency Badge checker. It pulls data from multiple public sources and shows you a clear result — in seconds, for free, with no registration.
            </p>
            <p className="text-muted-foreground">
              But we noticed another problem: legitimate new websites were being flagged as "Unknown" by our own tool — simply because their domain was new. A real online store, a freelancer's portfolio, a startup's landing page — all showing yellow warnings. That wasn't fair. So we created the Verified Badge Program — a way for website owners to apply for a one-time eligibility check and, if approved, show visitors that their site is verified and under continuous monitoring.
            </p>
          </div>
        </div>
      </section>

      {/* Two sides */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container max-w-4xl">
          <h2 className="text-2xl font-bold text-foreground text-center mb-10">Two Services, One Mission</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card rounded-2xl border p-7 space-y-4">
              <div className="w-10 h-10 rounded-full bg-safe/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-safe" />
              </div>
              <h3 className="text-lg font-bold text-foreground">For Everyone — Free Website Checker</h3>
              <p className="text-muted-foreground text-sm">
                Paste any URL and get a safety report in seconds. We check threat databases, domain age, SSL certificates, and more. No account needed, no cost, no limits. Built as a public safety tool.
              </p>
              <ul className="space-y-2">
                {[
                  "Check any website for free",
                  "No registration or account needed",
                  "Scans against real threat intelligence data",
                  "Clear, honest results you can understand",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-3.5 w-3.5 text-safe shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card rounded-2xl border-2 border-primary/30 p-7 space-y-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <BadgeCheck className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground">For Website Owners — Verified Badge</h3>
              <p className="text-muted-foreground text-sm">
                If you run a legitimate website and want to build trust with visitors, apply for the Verified Badge. Your site goes through a one-time eligibility check, and if it passes, your domain shows as "Verified" on our checker — plus you get an embeddable trust badge.
              </p>
              <ul className="space-y-2">
                {[
                  "One-time eligibility check by our team",
                  "Your checker result upgrades to 'Verified'",
                  "Embeddable trust badge for your site",
                  "$10/month per domain — first month free",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-3.5 w-3.5 text-primary shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/get-verified" className="inline-flex items-center gap-1 text-sm text-primary font-medium underline underline-offset-2">
                Apply for the Verified Badge →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
            <BookOpen className="h-6 w-6 text-primary" />
            How This Started
          </h2>
          <div className="bg-card rounded-2xl border p-8 md:p-10 space-y-4">
            <p className="text-muted-foreground">
              I'm Shaik Faizaan, an independent developer based in Hyderabad, India. I started this project after seeing friends and family members fall for scam websites — fake online stores, phishing pages, and too-good-to-be-true offers that looked completely real.
            </p>
            <p className="text-muted-foreground">
              I realized that the information needed to spot these scams already existed — in public threat databases, domain records, and SSL registries. But it was scattered, technical, and inaccessible to regular people. So I built a tool that pulls it all together into a simple report anyone can understand.
            </p>
            <p className="text-muted-foreground">
              As the tool grew, I heard from website owners who had a different problem: their new, legitimate websites were being flagged as "Unknown" — costing them sales and trust. That's when I created the Verified Badge Program, giving honest site owners a way to prove themselves through a standardized eligibility check and ongoing monitoring.
            </p>
            <p className="text-muted-foreground">
              Today, the Transparency Badge serves both sides of the internet — people who need to check websites before trusting them, and website owners who deserve to be trusted.
            </p>
            <div className="border-t pt-4 mt-2">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Shaik Faizaan</span> — Founder
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                📍 Hyderabad, India &nbsp;·&nbsp;
                <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline">hello@transparencybadge.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container">
          <SectionHeader
            title="What We Stand For"
            description="These aren't marketing buzzwords. They're the actual principles behind every decision we make."
            align="center"
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mt-12 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-card rounded-xl border p-6 text-center transition-all hover:shadow-elevated animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="font-semibold text-foreground mb-2">{value.title}</h3>
                <p className="text-sm text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Target className="h-6 w-6 text-primary" />
            What We Actually Check
          </h2>
          <div className="bg-card rounded-2xl border p-8 md:p-10 space-y-4">
            <p className="text-muted-foreground">
              When you check a website using our tool, here's what happens behind the scenes:
            </p>
            <ul className="space-y-3">
              {[
                "SSL & HTTPS — Is the connection encrypted? Is the certificate valid?",
                "Domain age — When was this domain registered? Is it hours old or years old?",
                "Threat intelligence — Is the domain in any public phishing, malware, or spam databases?",
                "Server location — Where is the website hosted? Does it match what the site claims?",
                "Reputation databases — Has anyone reported this domain for abuse?",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-muted-foreground">
              All of this is free and automatic. For website owners who want a higher level of trust, our Verified Badge Program offers a one-time eligibility check plus ongoing monitoring, at $10/month per domain.
            </p>
          </div>
        </div>
      </section>

      {/* What We're Not */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container max-w-3xl">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">What We Don't Do</h2>
          <div className="bg-card rounded-xl border divide-y">
            {[
              {
                title: "We Don't Replace Your Judgment",
                description: "We give you data. You make the decision. No tool can replace common sense and critical thinking.",
              },
              {
                title: "We're Not Perfect",
                description: "Our data sources have gaps. Threat feeds don't catch every scam instantly. We're upfront about that because transparency applies to us too.",
              },
              {
                title: "We Don't Guarantee Safety",
                description: "Even a 'Verified' badge doesn't mean a website can never have issues. That's why we monitor verified sites and revoke badges when needed.",
              },
              {
                title: "We Don't Make Legal Claims",
                description: "Our results reflect public data and our published eligibility criteria — not legal or regulatory judgments. We're a safety tool, not a court.",
              },
            ].map((item, index) => (
              <div key={index} className="p-5">
                <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">
          <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
            <Users className="h-6 w-6 text-primary" />
            Who Uses This Tool
          </h2>
          <div className="bg-card rounded-2xl border p-8 md:p-10 space-y-4">
            <p className="text-muted-foreground">Our tool is used by a wide range of people — all with the same question: "Is this website safe?"</p>
            <ul className="space-y-3">
              {[
                "Online shoppers checking unfamiliar stores before entering payment details",
                "Parents verifying websites their kids want to use",
                "Freelancers and remote workers checking job platforms before applying",
                "Small business owners who want to verify competitor or partner sites",
                "Website owners applying for the Verified Badge to build visitor trust",
                "Anyone who received a link via email, WhatsApp, or social media and wants to check it first",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 border-t">
        <div className="container text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Ready to Check a Website?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Use our free checker to verify any website in seconds — or apply for a Verified Badge if you own a legitimate website.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="btn-orange">
              <Link to="/"><Search className="mr-2 h-4 w-4" />Check a Website — Free</Link>
            </Button>
            <Button asChild size="lg" className="btn-verified">
              <Link to="/get-verified">Get Your Site Verified</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
